/**
 * Géométrie du panache : ce feu envoie-t-il sa fumée chez moi, et quand ?
 *
 * Deux notions distinctes cohabitent ici, et il faut les garder distinctes.
 *
 *   - `toward` décrit le panache PHYSIQUE, étroit. Il sert aux heures
 *     d'arrivée et au tracé de la carte, qui demandent la meilleure estimation
 *     possible de la trajectoire réelle.
 *
 *   - `couldReach` élargit ce panache de l'incertitude de direction du modèle
 *     météo. Il sert aux décisions de prudence, lesquelles ne doivent jamais
 *     reposer sur une direction supposée connue à quelques degrés près.
 */
import {
  ARRIVAL_MAX_HOURS,
  ARRIVAL_MIN_SPEED_KMH,
  ARRIVAL_ROUNDING_MIN,
  PLUME_BASE_KM,
  PLUME_MAX_KM,
  PLUME_SPREAD,
  WIND_DIRECTION_UNCERTAINTY_DEG,
  WIND_MIN_KMH,
} from './constants';
import { roundTimeToStep } from './format';
import { bearingDeg, distanceKm, type LocalFrame } from './geo';
import type {
  ArrivalUnavailable,
  FireZone,
  LatLon,
  LatLonTuple,
  PlumeAssessment,
  WindRegimes,
} from './types';
import { levelForRadius, regimeFor } from './wind';
import { nearestPointOf } from './zones';

const MS_PER_HOUR = 3_600_000;

/**
 * Élargissement latéral dû à l'incertitude de direction, par kilomètre parcouru.
 *
 * Une erreur de 20 degrés sur la direction déplace le panache de
 * `tan(20 degres)` fois la distance, soit environ 360 m par kilomètre. À 15 km,
 * cela fait déjà 5,5 km de côté : plus que la largeur du panache lui-même.
 * C'est précisément pour cela qu'un feu jugé "hors de l'axe" à courte distance
 * ne peut pas être écarté.
 */
const UNCERTAINTY_SPREAD = Math.tan((WIND_DIRECTION_UNCERTAINTY_DEG * Math.PI) / 180);

export interface PlumeContext {
  origin: LatLon;
  frame: LocalFrame;
  regimes: WindRegimes;
  /** Instant de référence, passé explicitement pour que tout reste testable. */
  now: number;
}

/** Demi-largeur du panache physique à une distance donnée, hors étendue du foyer. */
export function plumeHalfWidthKm(alongKm: number): number {
  return PLUME_BASE_KM + alongKm * PLUME_SPREAD;
}

/** Demi-largeur une fois ajoutée l'incertitude de direction du modèle. */
export function uncertainHalfWidthKm(alongKm: number): number {
  return plumeHalfWidthKm(alongKm) + alongKm * UNCERTAINTY_SPREAD;
}

/**
 * Ce feu est-il sur l'axe du vent, c'est-à-dire la flèche prolongée
 * traverse-t-elle sa zone rouge ?
 *
 *   - `along` : distance le long de l'axe, en remontant le vent
 *   - `cross` : de combien le feu est décalé sur le côté de cet axe
 *
 * Il y a fumée si le décalage est plus petit que la largeur du panache à cette
 * distance, largeur qui tient compte de l'étendue de la zone. Tester un simple
 * écart d'angle donnerait des fausses alertes sur un feu proche mais
 * franchement décalé.
 */
export function assessPlume(zone: FireZone, ctx: PlumeContext): PlumeAssessment | null {
  // L'altitude dépend de la taille du feu : gros foyer, panache haut, vent fort.
  const level = levelForRadius(zone.radiusKm);
  const regime = regimeFor(ctx.regimes, level.key);
  if (!regime) return null;

  /*
   * Tout se calcule depuis le BORD le plus proche du foyer, jamais son centre.
   *
   * Pour l'incendie du massif landais, le barycentre peut se trouver à quarante
   * kilomètres alors que le front de flammes est à cinq. Mesurer depuis le
   * centre donnait une durée de trajet huit fois trop longue et une distance
   * rassurante à tort.
   */
  const edge = nearestPointOf(zone, ctx.origin.lat, ctx.origin.lon);

  const common = {
    distanceKm: distanceKm(ctx.origin.lat, ctx.origin.lon, edge.lat, edge.lon),
    bearing: bearingDeg(ctx.origin.lat, ctx.origin.lon, edge.lat, edge.lon),
    level,
    windSpeed: regime.speed,
    windDirection: regime.direction,
    steadySince: regime.steadySince,
    turned: regime.turned,
  };

  const unknown = {
    alongKm: null,
    crossKm: null,
    halfWidthKm: null,
    uncertainHalfWidthKm: null,
    travelHours: null,
    hoursSinceShift: null,
    established: false,
    arrival: null,
  } as const;

  if (regime.speed !== null && regime.speed < WIND_MIN_KMH) {
    return {
      ...common,
      ...unknown,
      toward: false,
      couldReach: false,
      weakWind: true,
      arrivalUnavailable: 'too-slow',
    };
  }

  /*
   * Vecteur unitaire vers l'amont. La direction du vent est, par convention
   * météorologique, celle d'où vient l'air : ce vecteur pointe donc vers les
   * feux susceptibles de nous envoyer leur fumée.
   */
  const rad = (regime.direction * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);

  const local = ctx.frame.toLocal(edge.lat, edge.lon);
  const along = local.x * ux + local.y * uy;
  const cross = Math.abs(local.x * uy - local.y * ux);

  // Sous le vent, ou trop loin pour que la fumée soit encore concentrée.
  if (along <= 0 || along > PLUME_MAX_KM) {
    return {
      ...common,
      ...unknown,
      toward: false,
      couldReach: false,
      weakWind: false,
      alongKm: along,
      crossKm: cross,
      arrivalUnavailable: 'not-toward',
    };
  }

  const halfWidth = zone.radiusKm + plumeHalfWidthKm(along);
  const widened = zone.radiusKm + uncertainHalfWidthKm(along);
  const toward = cross <= halfWidth;
  const couldReach = cross <= widened;

  const speed = regime.speed;
  const travelHours = toward && speed !== null && speed > 0 ? along / speed : null;

  /*
   * Le compte à rebours ne démarre pas maintenant, mais au moment où le vent a
   * pris sa direction actuelle : avant ça, le panache partait ailleurs.
   *
   * Et l'heure n'est annoncée que lorsqu'elle veut dire quelque chose. Trois
   * cas la rendent muette, et chacun est nommé pour que l'interface puisse
   * l'expliquer au lieu de laisser croire à un régime établi.
   */
  let hoursSinceShift: number | null = null;
  let established = false;
  let arrival: number | null = null;
  let arrivalUnavailable: ArrivalUnavailable | null = toward ? null : 'not-toward';

  if (toward && travelHours !== null) {
    hoursSinceShift = (ctx.now - regime.steadySince) / MS_PER_HOUR;
    established = hoursSinceShift >= travelHours;

    if (established) {
      arrivalUnavailable = 'established';
    } else if (speed === null || speed < ARRIVAL_MIN_SPEED_KMH) {
      // Sous cette vitesse, une durée de trajet calculée est une fiction.
      arrivalUnavailable = 'too-slow';
    } else if (travelHours > ARRIVAL_MAX_HOURS) {
      // Le vent aura tourné plusieurs fois avant l'échéance annoncée.
      arrivalUnavailable = 'too-far-ahead';
    } else {
      arrival = roundTimeToStep(
        regime.steadySince + travelHours * MS_PER_HOUR,
        ARRIVAL_ROUNDING_MIN,
      );
    }
  }

  return {
    ...common,
    toward,
    couldReach,
    weakWind: false,
    alongKm: along,
    crossKm: cross,
    halfWidthKm: halfWidth,
    uncertainHalfWidthKm: widened,
    travelHours,
    hoursSinceShift,
    established,
    arrival,
    arrivalUnavailable,
  };
}

export interface Corridor {
  /** Polygone du panache physique, tel que le calcul le retient. */
  outline: LatLonTuple[];
  /** Polygone élargi par l'incertitude de direction, à tracer en arrière-plan. */
  uncertainOutline: LatLonTuple[];
  /** Extrémité de l'axe, pour la ligne médiane. */
  tip: LatLonTuple;
}

/**
 * Le couloir du vent tel qu'il apparaît sur la carte.
 *
 * Deux enveloppes sont renvoyées, et la carte doit tracer les deux. Ne montrer
 * que la bande étroite laissait croire que la direction du vent est connue
 * exactement, alors qu'un écart de 20 degrés suffit à changer la liste des
 * feux concernés.
 */
export function windCorridor(frame: LocalFrame, direction: number, stepKm = 10): Corridor {
  const rad = (direction * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  // Perpendiculaire à l'axe.
  const px = uy;
  const py = -ux;

  const build = (widthAt: (alongKm: number) => number): LatLonTuple[] => {
    const left: LatLonTuple[] = [];
    const right: LatLonTuple[] = [];
    for (let d = 0; d <= PLUME_MAX_KM; d += stepKm) {
      const w = widthAt(d);
      left.push(frame.toLatLon(ux * d + px * w, uy * d + py * w));
      right.push(frame.toLatLon(ux * d - px * w, uy * d - py * w));
    }
    return [...left, ...right.reverse()];
  };

  return {
    outline: build(plumeHalfWidthKm),
    uncertainOutline: build(uncertainHalfWidthKm),
    tip: frame.toLatLon(ux * PLUME_MAX_KM, uy * PLUME_MAX_KM),
  };
}
